export interface VideoFacts {
  durationSec: number;
  width: number;
  height: number;
}

/**
 * Reads duration and dimensions without uploading anything, so an over-long
 * clip is refused before it costs bandwidth. The server checks again, because
 * this number comes from the client.
 */
export function inspectVideo(file: File): Promise<VideoFacts> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;

    const done = (fn: () => void) => {
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      fn();
    };

    video.onloadedmetadata = () =>
      done(() =>
        resolve({
          durationSec: Number.isFinite(video.duration) ? video.duration : 0,
          width: video.videoWidth,
          height: video.videoHeight,
        }),
      );
    video.onerror = () =>
      done(() => reject(new Error('that video could not be read')));

    video.src = url;
  });
}

export interface VideoCheck {
  ok: boolean;
  reason?: string;
}

export function checkVideo(
  file: File,
  facts: VideoFacts,
  limits: { maxFileBytes: number; maxVideoSeconds: number },
): VideoCheck {
  if (file.size > limits.maxFileBytes) {
    return {
      ok: false,
      reason: `That clip is ${(file.size / 1e6).toFixed(0)} MB, over the ${Math.round(
        limits.maxFileBytes / 1e6,
      )} MB limit.`,
    };
  }
  if (facts.durationSec > limits.maxVideoSeconds) {
    return {
      ok: false,
      reason: `That clip is ${Math.round(facts.durationSec)} seconds, over the ${
        limits.maxVideoSeconds
      } second limit.`,
    };
  }
  return { ok: true };
}
